export enum LinkComponentTypes {
  NavLink = "shared.nav-link",
  ExternalLink = "shared.external-link",
  LinkList = "shared.link-list",
}

export type LinkTarget = "_blank" | "_self" | "_parent" | "_top" | string;

export type SubLinkLinkIconType = "primary" | "secondary";

export type ArticleCategory = "blog" | "news" | "announcement";

export type SocialLinkType = "facebook" | "instagram" | "twitter" | "tiktok" | "linkedin" | "whatsapp" | "youtube";

export type ColorVariant = "default" | "primary" | "secondary" | "tertiary" | "quaternary";

export type SocialLink = {
  id: number;
  link: string;
  type: SocialLinkType;
};

export type NavLinkItem = {
  id: number;
  text: string;
  to: string;
  __component: LinkComponentTypes.NavLink;
};

export type ExternalLinkItem = {
  id: number;
  text: string;
  href: string;
  target: LinkTarget;
  icon?: IImage;
  color?: "default" | "primary";
  __component: LinkComponentTypes.ExternalLink;
};

export type LinkListItem = {
  id: number;
  text: string;
  list: ExternalLinkItem[];
  __component: LinkComponentTypes.LinkList;
};

export type NavigationItem = NavLinkItem | ExternalLinkItem | LinkListItem;

export interface ImageAttributes {
  alternativeText?: string | null;
  url: string;
  width: number;
  height: number;
  name: string;
}

export type TTitleDescription = {
  title: string;
  description?: string;
};

export type TTitleDescriptionImage = {
  title: string;
  description?: string;
  image?: IImage;
};

export type MediaAttributes = {
  url: string;
  name: string;
  hash: string;
  size: number;
  ext: string;
  createdAt: string;
  updatedAt: string;
};

export type MediaType = {
  data: {
    attributes: MediaAttributes;
  };
};

export interface MultipleImages {
  data?: {
    id: number;
    attributes: ImageAttributes;
  }[];
}

export interface IImage {
  data: {
    attributes: ImageAttributes;
    id?: string;
  };
}

export interface IImages {
  data: {
    attributes: ImageAttributes;
  }[];
}

export type PageData<T> = {
  id: number;
  attributes: {
    createdAt: string;
    updatedAt: string;
    publishedAt: string;
  } & T;
};

export type TSEOMetaSocialItem = {
  description: string;
  id: number;
  image?: IImage | null;
  socialNetwork: string;
  title: string;
};

export type TSEOComponentData = {
  id: number;
  canonicalURL?: string;
  keywords?: string;
  metaDescription?: string;
  metaImage?: IImage | null;
  metaRobots?: string;
  metaSocial?: TSEOMetaSocialItem[];
  metaTitle?: string;
  metaViewport?: string;
  structuredData?: Record<string, any>;
};

export type TButtonLink = {
  id?: string | number;
  text: string;
  href: string;
  target: LinkTarget;
  icon?: IImage;
  iconPosition?: "start" | "end";
  variant?: "transparent" | "default" | "primary" | "secondary" | "tertiary" | "quaternary";
};

export type TContentCard = {
  id: string | number;
  title: string;
  description?: string;
  icon?: IImage;
  buttonLink?: string;
  buttonText?: string;
  buttonTarget?: LinkTarget;
};

export type TCtaCard = {
  id: string | number;
  title: string;
  description: string;
  button: TButtonLink;
};

export type TAccordion = {
  id: string | Number;
  attributes: {
    title: string;
    description: string;
  };
};

export type TTestimonyCard = {
  id: number;
  testimony: string;
  title: string;
  subtitle: string;
  thumbnail: IImage;
  videoUrl: string;
};

export type FormFieldType = "string" | "number" | "bigint" | "boolean";
export type FormInputType = "email" | "text" | "tel" | "number" | "textarea" | "radio";

export type TFormField = {
  id: string;
  label: string;
  placeholder: string;
  type: FormFieldType;
  validations?: string;
  optional: boolean;
  name: string;
  inputType: FormInputType;
  options?: string[];
};

export type TFaq = {
  id: string;
  attributes: {
    title: string;
    description: string;
  };
};

export type ContactFormSubmitType = "whatsapp";

/**
 * Country code according to {@link https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2#Officially_assigned_code_elements ISO 3166}
 */
export enum CountryCode {
  ID = "ID",
}

export enum Locale {
  en = "en",
  idID = "id-ID",
}

export type Pagination = {
  limit: number;
  start: number;
  total: number;
};

export type Collection<T> = {
  meta: {
    pagination: Pagination;
  };
  data: T[];
};

export type CalculatorType = "fixedRate" | "calculatedRate";

export type TCalculatedProduct = {
  id: number;
  defaultMonth: number;
  monthInputType: "select" | "radio";
  months: MonthCard[];
  monthInputLabel?: string;
  totalSumLabel?: string;
  totalSumNote?: string;
  defaultAmount?: number;
  amountInputLabel?: string;
  amountInputPlaceholder?: string;
  currency: string;
  annualInterestInputLabel?: string;
  annualInterestInputPlaceholder?: string;
  isShowAnnualInterest?: boolean;
  taxAmount: number;
  taxAmountDescription?: string;
  taxAmountLabel?: string;
  buttonText: string;
  agreementText?: string;
  rateBeforeTaxLabel?: string;
  rateAfterTaxLabel?: string;
  offerExpires?: OfferExpiresBlock;
};

export type TFixedRate = { sum: number; percent: number };

export type TFixedProduct = {
  id: number;
  rateLabel?: string;
  totalSumLabel?: string;
  rates: TFixedRate[];
  defaultAmount?: number;
  amountInputLabel?: string;
  amountInputPlaceholder?: string;
  isShowSlider?: boolean;
  currency: string;
  phoneInputLabel?: string;
  phoneInputPlaceholder?: string;
  isShowPhone?: boolean;
  buttonText: string;
  agreementText?: string;
  offerExpires?: OfferExpiresBlock;
};

export type TProduct = {
  name: string;
  percentage?: number;
  subtext?: string;
  min: number;
  minLabel?: string;
  max: number;
  maxLabel?: string;
  step: number;
  calculatedRate?: TCalculatedProduct;
  fixedRate?: TFixedProduct;
  calculatorType: CalculatorType;
};

export type MonthCard = {
  id: number;
  title: string;
  monthCount: number;
  percent: number;
  isPercentVisible: boolean;
};

export type OfferExpiresBlock = {
  text: string;
  datetime: Date;
  daysTitle: string;
  hoursTitle: string;
  minTitle: string;
};

export type IconText = {
  id: number;
  icon?: IImage;
  text?: string;
};
